import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Mail, 
  Loader2, 
  TreePine, 
  CheckCircle2,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

const WorkerLoginPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  // Check for magic link token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyMagicLink(token);
    }
  }, [searchParams]);

  const verifyMagicLink = async (token) => {
    setVerifying(true);
    setVerificationError(null);
    
    try {
      const response = await fetch(`${API}/marketplace/workers/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store worker session
        localStorage.setItem('worker_token', data.session_token);
        localStorage.setItem('worker_id', data.worker?.worker_id || '');
        localStorage.setItem('worker_name', data.worker?.name || '');
        localStorage.setItem('worker_email', data.worker?.email || '');
        
        toast.success('Login successful!');
        navigate('/worker/dashboard');
      } else {
        setVerificationError(data.detail || 'Verification failed. Please request a new login link.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationError('Network error. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const requestMagicLink = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/marketplace/workers/request-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSent(true);
        toast.success('Login link sent! Check your email.');
        
        // For development: Store and show the magic link
        if (data.dev_token) {
          // Always use current origin to avoid internal cluster URL issues
          const fullLink = `${window.location.origin}/worker/verify?token=${data.dev_token}`;
          setMagicLink(fullLink);
          console.log('Magic link (dev only):', fullLink);
        } else if (data.dev_magic_link) {
          // Fallback: extract token from dev_magic_link and rebuild with current origin
          const tokenMatch = data.dev_magic_link.match(/token=([a-f0-9]+)/);
          if (tokenMatch) {
            const fullLink = `${window.location.origin}/worker/verify?token=${tokenMatch[1]}`;
            setMagicLink(fullLink);
            console.log('Magic link (dev only):', fullLink);
          }
        }
      } else {
        toast.error(data.detail || 'Failed to send login link');
      }
    } catch (error) {
      console.error('Login request error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Verification pending screen
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-green-950/10 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Verifying your login...</h2>
            <p className="text-muted-foreground">Please wait while we verify your identity.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verification error screen
  if (verificationError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-red-950/10 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Verification Failed</h2>
            <p className="text-muted-foreground mb-6">{verificationError}</p>
            <Button onClick={() => {
              setVerificationError(null);
              navigate('/worker/login');
            }}>
              Request New Login Link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Link sent success screen
  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-green-950/10 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Check Your Email!</h2>
            <p className="text-muted-foreground mb-2">
              We sent a login link to:
            </p>
            <p className="font-medium text-foreground mb-6">{email}</p>
            
            {/* Dev mode: Show clickable magic link */}
            {magicLink && (
              <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 font-medium">
                  🔧 Development Mode - Click to login:
                </p>
                <a 
                  href={magicLink}
                  className="text-sm text-primary hover:underline break-all"
                  data-testid="dev-magic-link"
                >
                  {magicLink}
                </a>
              </div>
            )}
            
            <p className="text-sm text-muted-foreground mb-6">
              Click the link in your email to access your worker dashboard. 
              The link expires in 1 hour.
            </p>
            <Button variant="outline" onClick={() => { setSent(false); setMagicLink(''); }}>
              Use Different Email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main login form
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-green-950/10 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card>
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <TreePine className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Worker Portal</CardTitle>
            <CardDescription>
              Sign in to access eco-task missions and submit proof-of-work
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={requestMagicLink} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="worker-email-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  We&apos;ll send you a magic link to sign in. No password needed.
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="worker-login-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Login Link
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t border-border">
              <h4 className="text-sm font-medium text-foreground mb-3">How it works:</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">1.</span>
                  Enter your email and click &quot;Send Login Link&quot;
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">2.</span>
                  Check your inbox for a magic link
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">3.</span>
                  Click the link to access your dashboard
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-primary">4.</span>
                  Claim missions and submit proof-of-work
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
        
        <p className="text-center text-sm text-muted-foreground mt-4">
          New to Epuyén Renace?{' '}
          <a href="/community" className="text-primary hover:underline">
            Learn about our mission
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default WorkerLoginPage;
